import React, { useState, useEffect } from 'react';

interface WaitlistEntry {
  id: string;
  email: string;
  createdAt: string;
}

export default function WaitlistManager() {
  const [waitlistData, setWaitlistData] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    document.body.classList.add('analytics-page');
    return () => {
      document.body.classList.remove('analytics-page');
    };
  }, []);

  useEffect(() => {
    fetchWaitlistData();
  }, []);

  const fetchWaitlistData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/waitlist/all', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch waitlist data');
      }

      const data = await response.json();
      setWaitlistData(data.waitlist || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Email', 'Signup Date'],
      ...waitlistData.map(entry => [
        entry.email,
        new Date(entry.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f7fafc', paddingTop: '60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div style={{ fontSize: '1.5rem', color: '#6b7280' }}>Loading waitlist data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f7fafc', paddingTop: '60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
          <div style={{ textAlign: 'center', padding: '4rem' }}>
            <div style={{ fontSize: '1.5rem', color: '#ef4444', marginBottom: '1rem' }}>Error</div>
            <div style={{ color: '#6b7280' }}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7fafc', paddingTop: '60px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1a202c' }}>
              📋 Waitlist Manager
            </h1>
            <button
              onClick={exportToCSV}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#059669';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#10b981';
              }}
            >
              Export to CSV
            </button>
          </div>
          <p style={{ color: '#6b7280', fontSize: '1rem' }}>
            Manage email signups from the landing page waitlist
          </p>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <div style={{ 
            backgroundColor: '#2d3748', 
            padding: '1.5rem', 
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
              {totalCount}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Total Signups
            </div>
          </div>
          <div style={{ 
            backgroundColor: '#2d3748', 
            padding: '1.5rem', 
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
              {waitlistData.filter(entry => {
                const signupDate = new Date(entry.createdAt);
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return signupDate >= weekAgo;
              }).length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              This Week
            </div>
          </div>
          <div style={{ 
            backgroundColor: '#2d3748', 
            padding: '1.5rem', 
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
              {waitlistData.filter(entry => {
                const signupDate = new Date(entry.createdAt);
                const today = new Date();
                return signupDate.toDateString() === today.toDateString();
              }).length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Today
            </div>
          </div>
        </div>

        <div style={{ 
          backgroundColor: '#2d3748', 
          borderRadius: '0.5rem', 
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ 
            backgroundColor: '#374151', 
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #4b5563'
          }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f9fafb', margin: 0 }}>
              Waitlist Entries ({totalCount})
            </h2>
          </div>

          {waitlistData.length === 0 ? (
            <div style={{ 
              padding: '3rem', 
              textAlign: 'center', 
              color: '#6b7280' 
            }}>
              No waitlist entries yet
            </div>
          ) : (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#374151' }}>
                    <th style={{ 
                      padding: '1rem 1.5rem', 
                      textAlign: 'left', 
                      color: '#f9fafb', 
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}>
                      Email Address
                    </th>
                    <th style={{ 
                      padding: '1rem 1.5rem', 
                      textAlign: 'left', 
                      color: '#f9fafb', 
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}>
                      Signup Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {waitlistData.map((entry, index) => (
                    <tr 
                      key={entry.id} 
                      style={{ 
                        backgroundColor: index % 2 === 0 ? '#2d3748' : '#374151',
                        borderBottom: '1px solid #4b5563'
                      }}
                    >
                      <td style={{ 
                        padding: '1rem 1.5rem', 
                        color: '#f9fafb',
                        fontSize: '0.875rem'
                      }}>
                        {entry.email}
                      </td>
                      <td style={{ 
                        padding: '1rem 1.5rem', 
                        color: '#d1d5db',
                        fontSize: '0.875rem'
                      }}>
                        {formatDate(entry.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}